import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiX, FiDownload, FiLoader, FiUploadCloud, FiInfo, FiRotateCcw, FiImage, FiLayers, FiSun, FiCrop, FiEye, FiZap, FiCheck, FiRepeat, FiEdit3, FiCornerUpLeft } from "react-icons/fi";
import { API_BASE_URL, apiRequest, errorMessage } from "../api";
import { getClientId, formatSeconds } from "./audioShared";

const MAX_FILE_BYTES = 25 * 1024 * 1024;
const cardClass = "rounded-2xl bg-white/5 border border-navy-border p-5";
const labelClass = "text-xs uppercase tracking-widest text-white/50 font-semibold block mb-3";
const ghost = "px-3.5 py-2 rounded-full border border-white/20 hover:border-accent-light text-xs font-medium inline-flex items-center gap-1.5 transition-colors disabled:opacity-50";
const chip = (on) => `px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${on ? "bg-accent-light text-navy border-accent-light" : "bg-white/5 border-navy-border text-gray-300 hover:border-accent-light"}`;
const CHECKER = "bg-[repeating-conic-gradient(#2a3350_0%_25%,#1a2136_0%_50%)] bg-[length:20px_20px]";

const COLOURS = [["#ffffff", "White"], ["#000000", "Black"], ["#f1f3f6", "Light grey"], ["#2f6fdc", "Passport blue"], ["#d6322e", "Red"], ["#17a35a", "Green"], ["#ffd23f", "Yellow"], ["#ff8fb1", "Pink"]];
const TABS = [["fix", "Fix cut-out", FiEdit3], ["background", "Background", FiLayers], ["subject", "Subject", FiImage], ["shadow", "Shadow & outline", FiSun], ["size", "Shape & size", FiCrop], ["download", "Download", FiDownload]];

const DEFAULT_SETTINGS = {
  background: { type: "transparent", color: "#ffffff", color2: "#6aa6ff", angle: 90, blur: 40 },
  subject: { edge: 0, feather: 2, flip: false, rotate: 0, scale: 100, x: 0, y: 0, brightness: 0, contrast: 0, saturation: 0, warmth: 0, sharpness: 0, grey: false, enhance: false, fill_holes: false },
  shadow: { type: "none", opacity: 45, blur: 12, x: 3, y: 5, color: "#000000" },
  outline: { width: 0, color: "#ffffff" },
  canvas: { mode: "keep", aspect: "original", padding: 8, size: "original" },
  edits: [],
};

function Slider({ label, value, min = 0, max = 100, step = 1, onChange, suffix = "" }) {
  return (
    <label className="block">
      <span className="flex items-center justify-between text-xs text-gray-300 mb-1.5">{label}<span className="text-gray-500 tabular-nums">{value}{suffix}</span></span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#22d3ee]" aria-label={label} />
    </label>
  );
}

function Colour({ label, value, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs text-gray-300">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-9 h-9 rounded-md border border-navy-border bg-transparent p-0.5 cursor-pointer" aria-label={label} />
      {label}
    </label>
  );
}

const nearestRatio = (w, h) => {
  const options = { "1:1": 1, "4:3": 4 / 3, "16:9": 16 / 9, "3:4": 3 / 4, "9:16": 9 / 16 };
  return Object.entries(options).sort((a, b) => Math.abs(w / h - a[1]) - Math.abs(w / h - b[1]))[0][0];
};

async function saveBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// AI Background Removal: upload a picture, the AI cuts out the subject, then change the background (colour, gradient, blur, your own
// picture or an AI-made scene), tidy the edge, add a shadow or outline, fit it to a shape (passport photo, square, 16:9) and download it.
export default function BackgroundRemovalTool() {
  const [clientId] = useState(getClientId);
  const [options, setOptions] = useState(null);
  const [picture, setPicture] = useState(null);                // { token, width, height, name, image }
  const [stage, setStage] = useState("upload");                // upload | removing | edit
  const [job, setJob] = useState(null);                        // the running removal
  const [elapsed, setElapsed] = useState(0);
  const [engine, setEngine] = useState("");
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [tab, setTab] = useState("background");
  const [brush, setBrush] = useState({ mode: "keep", size: 0.02 });
  const [refine, setRefine] = useState({ src: "", state: "idle" });
  const [hover, setHover] = useState(null);                    // brush ring position, as fractions of the picture
  const areaRef = useRef(null);
  const overlayRef = useRef(null);
  const strokeRef = useRef(null);
  const [bg, setBg] = useState({ token: "", name: "" });
  const [scene, setScene] = useState({ prompt: "", busy: false, message: "" });
  const [preview, setPreview] = useState({ src: "", state: "idle", message: "", transparent: true, width: 0, height: 0 });
  const [holding, setHolding] = useState(false);
  const [format, setFormat] = useState("png");
  const [exporting, setExporting] = useState(false);
  const [note, setNote] = useState("");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const bgInputRef = useRef(null);
  const requestId = useRef(0);

  useEffect(() => {
    let alive = true;
    apiRequest("/core/bgremove/options/").then(({ ok, data }) => { if (alive && ok) setOptions(data); });
    return () => { alive = false; };
  }, []);

  const patch = (section, values) => setSettings((s) => ({ ...s, [section]: { ...s[section], ...values } }));

  // ------------------------------------------------------------------------------------ upload and removal
  const startRemoval = useCallback(async (token, name, method) => {
    setError("");
    setStage("removing");
    setElapsed(0);
    setSettings((current) => ({ ...current, edits: [] }));
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "bgremove");
    form.append("token", token);
    form.append("name", name);
    form.append("method", method);
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    if (!ok) { setStage("upload"); return setError(data?.token || errorMessage(data)); }
    setJob(data);
  }, [clientId]);

  const upload = async (file) => {
    setError("");
    if (!file) return;
    if (!file.type.startsWith("image/")) return setError("Choose a JPG, PNG or WebP picture.");
    if (file.size > MAX_FILE_BYTES) return setError("This picture is larger than 25 MB.");
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    const { ok, data } = await apiRequest("/core/bgremove/upload/", { method: "POST", body: form });
    setUploading(false);
    if (!ok) return setError(data?.file || errorMessage(data));
    setPicture(data);
    setSettings(DEFAULT_SETTINGS);
    setBg({ token: "", name: "" });
    setNote("");
    startRemoval(data.token, data.name, "auto");
  };

  // Poll the removal until it is finished.
  useEffect(() => {
    if (stage !== "removing" || !job) return undefined;
    const tick = setInterval(() => setElapsed((e) => e + 1), 1000);
    const poll = setInterval(async () => {
      const { ok, data } = await apiRequest(`/core/voice-clone/${job.id}/?client_id=${encodeURIComponent(clientId)}`);
      if (!ok) return;
      setJob(data);
      if (data.status === "completed") { setEngine(data.engine || ""); setStage("edit"); }
      if (data.status === "failed") { setError(data.error_message || "The background could not be removed."); setStage("failed"); }
    }, 2500);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [stage, job?.id, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------------------------------------------------------------ live preview
  const body = useCallback((extra = {}) => {
    const form = new FormData();
    form.append("token", picture.token);
    form.append("settings", JSON.stringify(settings));
    if (bg.token) form.append("bg_token", bg.token);
    Object.entries(extra).forEach(([k, v]) => form.append(k, v));
    return form;
  }, [picture, settings, bg]);

  useEffect(() => {
    if (stage !== "edit" || !picture) return undefined;
    const id = ++requestId.current;
    setPreview((p) => ({ ...p, state: "loading" }));
    const timer = setTimeout(async () => {
      const { ok, data } = await apiRequest("/core/bgremove/preview/", { method: "POST", body: body() });
      if (id !== requestId.current) return;
      if (!ok) return setPreview((p) => ({ ...p, state: "error", message: data?.detail || errorMessage(data) }));
      setPreview({ src: data.image, state: "ready", message: "", transparent: data.transparent, width: data.width, height: data.height });
    }, 250);
    return () => clearTimeout(timer);
  }, [stage, picture, body]);

  useEffect(() => {
    if (stage !== "edit" || !picture || tab !== "fix") return undefined;
    let alive = true;
    setRefine((r) => ({ ...r, state: "loading" }));
    const timer = setTimeout(async () => {
      const form = new FormData();
      form.append("token", picture.token);
      form.append("settings", JSON.stringify({ edits: settings.edits, subject: { fill_holes: settings.subject.fill_holes } }));
      const { ok, data } = await apiRequest("/core/bgremove/refine/", { method: "POST", body: form });
      if (!alive) return;
      setRefine(ok ? { src: data.image, state: "ready" } : { src: "", state: "error", message: data?.detail || errorMessage(data) });
    }, 120);
    return () => { alive = false; clearTimeout(timer); };
  }, [stage, picture, tab, settings.edits, settings.subject.fill_holes]);

  // ------------------------------------------------------------------------------------ the brush
  const pointOf = (event) => {
    const rect = areaRef.current.getBoundingClientRect();
    return [Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)), Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))];
  };
  const drawStroke = () => {
    const canvas = overlayRef.current;
    const stroke = strokeRef.current;
    if (!canvas || !stroke) return;
    const rect = areaRef.current.getBoundingClientRect();
    if (canvas.width !== Math.round(rect.width)) { canvas.width = Math.round(rect.width); canvas.height = Math.round(rect.height); }
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = stroke.mode === "keep" ? "rgba(34,197,94,0.6)" : "rgba(239,68,68,0.6)";
    context.fillStyle = context.strokeStyle;
    const radius = stroke.r * Math.max(rect.width, rect.height);
    context.lineWidth = radius * 2;
    context.beginPath();
    stroke.p.forEach(([x, y], i) => (i ? context.lineTo(x * rect.width, y * rect.height) : context.moveTo(x * rect.width, y * rect.height)));
    context.stroke();
    if (stroke.p.length === 1) { context.beginPath(); context.arc(stroke.p[0][0] * rect.width, stroke.p[0][1] * rect.height, radius, 0, Math.PI * 2); context.fill(); }
  };
  const brushDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    strokeRef.current = { m: brush.mode, mode: brush.mode, r: brush.size, p: [pointOf(event)] };
    drawStroke();
  };
  const brushMove = (event) => {
    const point = pointOf(event);
    setHover(point);
    const stroke = strokeRef.current;
    if (!stroke) return;
    const last = stroke.p[stroke.p.length - 1];
    if (Math.hypot(point[0] - last[0], point[1] - last[1]) > 0.003 && stroke.p.length < 590) { stroke.p.push(point); drawStroke(); }
  };
  const brushUp = () => {
    const stroke = strokeRef.current;
    strokeRef.current = null;
    if (!stroke) return;
    setSettings((current) => ({ ...current, edits: [...current.edits, { m: stroke.m, r: stroke.r, p: stroke.p }].slice(-290) }));
    setTimeout(() => { const canvas = overlayRef.current; if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height); }, 350);   // until the new view arrives
  };
  const undo = () => setSettings((current) => ({ ...current, edits: current.edits.slice(0, -1) }));

  // ------------------------------------------------------------------------------------ backgrounds
  const pickBackgroundPicture = async (file) => {
    setScene((s) => ({ ...s, message: "" }));
    if (!file) return;
    if (!file.type.startsWith("image/")) return setScene((s) => ({ ...s, message: "Choose a JPG, PNG or WebP picture." }));
    const form = new FormData();
    form.append("file", file);
    const { ok, data } = await apiRequest("/core/bgremove/upload/", { method: "POST", body: form });
    if (!ok) return setScene((s) => ({ ...s, message: data?.file || errorMessage(data) }));
    setBg({ token: data.token, name: file.name });
    patch("background", { type: "image" });
  };

  const makeScene = async () => {
    setScene((s) => ({ ...s, busy: true, message: "Drawing your background..." }));
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "image");
    form.append("prompt", scene.prompt);
    form.append("ratio", nearestRatio(picture.width, picture.height));
    form.append("style", "photo");
    form.append("count", "1");
    const started = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    if (!started.ok) return setScene((s) => ({ ...s, busy: false, message: started.data?.prompt || errorMessage(started.data) }));
    const id = started.data.id;
    let result = null;
    for (let i = 0; i < 100; i += 1) {
      await new Promise((r) => setTimeout(r, 3000));
      const { ok, data } = await apiRequest(`/core/voice-clone/${id}/?client_id=${encodeURIComponent(clientId)}`);
      if (ok && (data.status === "completed" || data.status === "failed")) { result = data; break; }
      if (ok && data.progress) setScene((s) => ({ ...s, message: `Drawing your background... ${data.progress}` }));
    }
    if (!result || result.status !== "completed" || !result.images?.length) {
      return setScene((s) => ({ ...s, busy: false, message: result?.error_message || "The background could not be drawn right now. Please try again." }));
    }
    try {
      const blob = await (await fetch(result.images[0].url)).blob();
      const form2 = new FormData();
      form2.append("file", new File([blob], "scene.png", { type: blob.type || "image/png" }));
      const uploaded = await apiRequest("/core/bgremove/upload/", { method: "POST", body: form2 });
      if (!uploaded.ok) throw new Error("upload");
      setBg({ token: uploaded.data.token, name: "AI background" });
      patch("background", { type: "image" });
      setScene((s) => ({ ...s, busy: false, message: "" }));
    } catch {
      setScene((s) => ({ ...s, busy: false, message: "The background was drawn but could not be loaded. Please try again." }));
    }
    apiRequest(`/core/voice-clone/${id}/?client_id=${encodeURIComponent(clientId)}`, { method: "DELETE" });      // the page keeps its own copy
  };

  // ------------------------------------------------------------------------------------ download
  const download = async () => {
    setExporting(true);
    setError("");
    setNote("");
    try {
      const response = await fetch(`${API_BASE_URL}/core/bgremove/export/`, { method: "POST", body: body({ format, name: picture.name }) });
      if (!response.ok) {
        let data = null;
        try { data = await response.json(); } catch { /* not JSON */ }
        throw new Error(data?.detail || "The picture could not be made. Please try again.");
      }
      const disposition = response.headers.get("Content-Disposition") || "";
      const filename = (disposition.match(/filename="([^"]+)"/) || [])[1] || `picture-no-background.${format}`;
      setNote([response.headers.get("X-Image-Size") && `Saved ${response.headers.get("X-Image-Size").replace("x", " × ")} px`, response.headers.get("X-Note")].filter(Boolean).join(" · "));
      await saveBlob(filename, await response.blob());
    } catch (exc) {
      setError(exc.message);
    }
    setExporting(false);
  };

  const reset = () => { setSettings(DEFAULT_SETTINGS); setBg({ token: "", name: "" }); };
  const s = settings;
  const sizeLine = preview.width ? `${preview.width} × ${preview.height} (preview)` : "";

  // =====================================================================================================
  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Background Removal</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-10">
        Upload a picture and the AI cuts out the subject. Then choose a new background - a colour, gradient, blur, your own picture or one the AI draws - tidy the edge,
        add a shadow, fit it to a passport or social-media shape, and download it with a transparent or coloured background.
      </p>

      {(stage === "upload" || stage === "failed") && (
        <div className={cardClass}>
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files?.[0]); }}
            role="button" tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-16 text-center transition-colors ${dragging ? "border-accent-light bg-accent/10" : "border-navy-border hover:border-accent-light/70"}`}
          >
            {uploading ? <FiLoader className="mx-auto text-accent-light animate-spin" size={34} /> : <FiUploadCloud className="mx-auto text-accent-light" size={34} />}
            <p className="mt-3 text-base text-gray-100">{uploading ? "Opening your picture..." : "Click to choose a picture, or drop it here"}</p>
            <p className="text-xs text-gray-500 mt-1">JPG, PNG or WebP - up to 25 MB. Works on people, products, pets and logos.</p>
          </div>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          {error && (
            <div className="mt-5 text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
              <p className="flex items-start gap-2"><FiInfo className="mt-0.5 shrink-0" />{error}</p>
              {stage === "failed" && picture && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <button type="button" onClick={() => startRemoval(picture.token, picture.name, "auto")} className={ghost}><FiRepeat size={13} /> Try again</button>
                  <button type="button" onClick={() => startRemoval(picture.token, picture.name, "simple")} className={ghost}>Use the simple mode (plain backgrounds)</button>
                </div>
              )}
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8 text-sm">
            {[["Cut out", "The AI finds the subject and removes everything else, keeping hair and fur edges."], ["New background", "Colour, gradient, blurred original, your own picture, or an AI scene from a description."],
              ["Tidy & style", "Shrink or soften the edge, shadows, a sticker outline, colours, flip, rotate, resize."], ["Fit & download", "Passport 35×45, square, 4:5, 16:9, 9:16 - as PNG, WebP or JPG."]].map(([title, text]) => (
              <div key={title} className="rounded-xl bg-navy border border-navy-border p-4"><p className="font-semibold mb-1">{title}</p><p className="text-xs text-gray-400 leading-relaxed">{text}</p></div>
            ))}
          </div>
        </div>
      )}

      {stage === "removing" && picture && (
        <div className={`${cardClass} text-center`}>
          <div className="relative inline-block rounded-xl overflow-hidden max-w-full">
            <img src={picture.image} alt="Your upload" className="max-h-[50vh] w-auto max-w-full opacity-60" />
            <div className="absolute inset-0 flex items-center justify-center"><FiLoader className="animate-spin text-accent-light" size={40} /></div>
          </div>
          <p className="font-semibold mt-5">Removing the background... {formatSeconds(elapsed)}{job?.progress && <span className="text-gray-400 font-normal"> · {job.progress}</span>}</p>
          <p className="text-sm text-gray-400 mt-1">Usually 10-30 seconds. The free AI service is sometimes busy; this page waits for it.</p>
        </div>
      )}

      {stage === "edit" && picture && (
        <div className="grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-6">
          <div className={cardClass}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <p className="text-sm font-semibold truncate max-w-[55%]" title={picture.name}>{picture.name} <span className="text-xs text-gray-500 font-normal">{picture.width}×{picture.height}</span></p>
              <div className="flex items-center gap-2">
                <button type="button" onMouseDown={() => setHolding(true)} onMouseUp={() => setHolding(false)} onMouseLeave={() => setHolding(false)}
                  onTouchStart={() => setHolding(true)} onTouchEnd={() => setHolding(false)} className={ghost}><FiEye size={13} /> Hold to see original</button>
                <button type="button" onClick={() => { setStage("upload"); setPicture(null); setJob(null); setPreview({ src: "", state: "idle", message: "", transparent: true, width: 0, height: 0 }); }} className={ghost}><FiX size={13} /> New picture</button>
              </div>
            </div>
            {tab === "fix" ? (
              <div className="rounded-xl overflow-hidden bg-navy flex items-center justify-center min-h-[260px] p-2">
                <div ref={areaRef} className="relative inline-block touch-none select-none cursor-none" onPointerDown={brushDown} onPointerMove={brushMove} onPointerUp={brushUp}
                  onPointerCancel={brushUp} onPointerLeave={() => setHover(null)} role="application" aria-label="Brush area: paint over the picture to restore or erase parts">
                  {refine.src ? <img src={refine.src} alt="Cut-out with the removed parts dimmed in red" draggable={false} className="block max-h-[68vh] w-auto max-w-full" /> : <div className="w-72 h-56 flex items-center justify-center text-gray-500"><FiLoader className="animate-spin" /></div>}
                  <canvas ref={overlayRef} className="absolute inset-0 w-full h-full pointer-events-none" />
                  {hover && (
                    <span className="absolute pointer-events-none rounded-full border-2 -translate-x-1/2 -translate-y-1/2"
                      style={{ left: `${hover[0] * 100}%`, top: `${hover[1] * 100}%`, width: `${brush.size * 200 * Math.max(picture.width, picture.height) / picture.width}%`, aspectRatio: "1",
                        borderColor: brush.mode === "keep" ? "#22c55e" : "#ef4444" }} />
                  )}
                  {refine.state === "loading" && <span className="absolute top-2 right-2 px-2.5 py-1 rounded-full bg-black/60 text-xs text-white inline-flex items-center gap-1.5"><FiLoader className="animate-spin" size={12} /> Updating</span>}
                </div>
              </div>
            ) : (
            <div className={`relative rounded-xl overflow-hidden flex items-center justify-center min-h-[260px] ${preview.transparent && !holding ? CHECKER : "bg-navy"}`}>
              {(holding ? picture.image : preview.src) && <img src={holding ? picture.image : preview.src} alt={holding ? "Your original" : "Live preview of the result"} className="max-h-[70vh] w-auto max-w-full object-contain" />}
              {preview.state === "loading" && !holding && <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/60 text-xs text-white inline-flex items-center gap-1.5"><FiLoader className="animate-spin" size={12} /> Updating</span>}
              {holding && <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-xs text-white">Original</span>}
            </div>
            )}
            {preview.state === "error" && <p className="text-sm text-amber-300 mt-3">{preview.message}</p>}
            <p className="text-xs text-gray-500 mt-3">{engine && <>Cut out by: {engine}. </>}{sizeLine && <>{sizeLine}. </>}The chequered pattern means transparent.
              {tab !== "fix" && <> Part of the subject missing? <button type="button" onClick={() => setTab("fix")} className="text-accent-light underline">Fix the cut-out</button>.</>}</p>
            {engine.startsWith("Simple") && <p className="text-xs text-amber-200 mt-2 flex items-start gap-1.5"><FiInfo className="mt-0.5 shrink-0" size={12} />The simple mode only detects a plain background. For this picture the AI remover was busy; the edge may be rough.
              <button type="button" onClick={() => startRemoval(picture.token, picture.name, "ai")} className="underline ms-1">Try the AI again</button></p>}
          </div>

          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              {TABS.map(([key, label, Icon]) => (
                <button key={key} type="button" onClick={() => setTab(key)} aria-pressed={tab === key}
                  className={`px-3.5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${tab === key ? "bg-brand-gradient text-white shadow-md shadow-accent/20" : "bg-white/5 border border-navy-border text-gray-300 hover:border-accent-light"}`}>
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>

            {tab === "fix" && (
              <div className={`${cardClass} space-y-5`}>
                <p className="text-sm text-gray-300 leading-relaxed">The AI sometimes cuts away arms, hands, hair or other parts that belong to the subject. In the picture on the left, <strong className="text-red-300">what was removed is dimmed in red</strong>.
                  Paint over a missing part with <strong className="text-emerald-300">Restore</strong>, or over leftover background with <strong className="text-red-300">Erase</strong>.</p>
                <div className="flex gap-2">
                  {[["keep", "Restore (paint back)"], ["remove", "Erase (remove)"]].map(([key, label]) => (
                    <button key={key} type="button" onClick={() => setBrush((b) => ({ ...b, mode: key }))} aria-pressed={brush.mode === key}
                      className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${brush.mode === key ? (key === "keep" ? "bg-emerald-500 text-white border-emerald-400" : "bg-red-500 text-white border-red-400") : "bg-white/5 border-navy-border text-gray-300 hover:border-accent-light"}`}>{label}</button>
                  ))}
                </div>
                <Slider label="Brush size" value={Math.round(brush.size * 1000) / 10} min={0.5} max={12} step={0.5} onChange={(v) => setBrush((b) => ({ ...b, size: v / 100 }))} suffix="%" />
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={undo} disabled={!settings.edits.length} className={ghost}><FiCornerUpLeft size={13} /> Undo last stroke</button>
                  <button type="button" onClick={() => setSettings((c) => ({ ...c, edits: [] }))} disabled={!settings.edits.length} className={ghost}><FiRotateCcw size={13} /> Clear my strokes ({settings.edits.length})</button>
                </div>
                <label className="flex items-start gap-3 text-sm text-gray-200 cursor-pointer border-t border-navy-border pt-5">
                  <input type="checkbox" checked={settings.subject.fill_holes} onChange={(e) => patch("subject", { fill_holes: e.target.checked })} className="mt-1 accent-[#22d3ee] w-4 h-4" />
                  <span>Fill holes inside the subject <span className="block text-xs text-gray-500">Closes gaps the AI left in the middle of the subject (for example between an arm and the body). Large openings stay open.</span></span>
                </label>
                <div className="border-t border-navy-border pt-5 flex flex-wrap gap-2 items-center">
                  <button type="button" onClick={() => startRemoval(picture.token, picture.name, "ai")} className={ghost}><FiRepeat size={13} /> Cut out again with the AI</button>
                  <button type="button" onClick={() => startRemoval(picture.token, picture.name, "simple")} className={ghost}>Plain-background mode</button>
                </div>
                <p className="text-xs text-gray-500">Tip: paint with a small brush near edges and zoom your browser (Ctrl +) for fine work. The same strokes are used in the final download, at full size.</p>
              </div>
            )}

            {tab === "background" && (
              <div className={`${cardClass} space-y-5`}>
                <div className="flex flex-wrap gap-2">
                  {[["transparent", "Transparent"], ["color", "Colour"], ["gradient", "Gradient"], ["blur", "Blurred original"], ["image", "Picture / AI scene"]].map(([key, label]) => (
                    <button key={key} type="button" onClick={() => patch("background", { type: key })} aria-pressed={s.background.type === key} className={chip(s.background.type === key)}>{label}</button>
                  ))}
                </div>
                {s.background.type === "color" && (
                  <div>
                    <span className={labelClass}>Colour</span>
                    <div className="flex flex-wrap gap-2.5 items-center">
                      {COLOURS.map(([value, name]) => (
                        <button key={value} type="button" title={name} aria-label={name} onClick={() => patch("background", { color: value })}
                          className={`w-9 h-9 rounded-full border-2 ${s.background.color.toLowerCase() === value ? "border-white" : "border-white/20"}`} style={{ background: value }} />
                      ))}
                      <Colour label="Custom" value={s.background.color} onChange={(v) => patch("background", { color: v })} />
                    </div>
                    <p className="text-xs text-gray-500 mt-3">For passport and visa photos pick white, light grey or blue, then choose "Passport 35×45 mm" under Shape &amp; size.</p>
                  </div>
                )}
                {s.background.type === "gradient" && (
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-4"><Colour label="From" value={s.background.color} onChange={(v) => patch("background", { color: v })} /><Colour label="To" value={s.background.color2} onChange={(v) => patch("background", { color2: v })} /></div>
                    <Slider label="Direction" value={s.background.angle} min={0} max={360} onChange={(v) => patch("background", { angle: v })} suffix="°" />
                    <div className="flex flex-wrap gap-2">{[["#ff9a5a", "#7b3fe4", "Sunset"], ["#56ccf2", "#2f80ed", "Ocean"], ["#f2f2f2", "#cfd9df", "Studio"], ["#11998e", "#38ef7d", "Fresh"], ["#232526", "#414345", "Charcoal"]].map(([a, b, name]) => (
                      <button key={name} type="button" onClick={() => patch("background", { color: a, color2: b })} className="px-3 py-1.5 rounded-full text-xs text-white border border-white/20" style={{ background: `linear-gradient(90deg, ${a}, ${b})`, textShadow: "0 1px 2px #0008" }}>{name}</button>
                    ))}</div>
                  </div>
                )}
                {s.background.type === "blur" && <Slider label="Blur" value={s.background.blur} onChange={(v) => patch("background", { blur: v })} />}
                {s.background.type === "image" && (
                  <div className="space-y-4">
                    <div>
                      <span className={labelClass}>Your own picture</span>
                      <input ref={bgInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { pickBackgroundPicture(e.target.files?.[0]); e.target.value = ""; }} />
                      <button type="button" onClick={() => bgInputRef.current?.click()} className={ghost}><FiUploadCloud size={13} /> {bg.token ? "Choose another picture" : "Choose a background picture"}</button>
                      {bg.token && <span className="ms-3 text-xs text-gray-400 inline-flex items-center gap-1"><FiCheck size={12} className="text-emerald-400" /> {bg.name}</span>}
                    </div>
                    <div>
                      <span className={labelClass}>Or describe a background and the AI draws it</span>
                      <div className="flex gap-2">
                        <input value={scene.prompt} onChange={(e) => setScene((v) => ({ ...v, prompt: e.target.value.slice(0, 300) }))} placeholder="e.g. a sunny beach with palm trees" dir="auto"
                          className="flex-1 px-3.5 py-2.5 rounded-lg bg-navy border border-navy-border text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-accent-light" />
                        <button type="button" onClick={makeScene} disabled={scene.busy || scene.prompt.trim().length < 3} className={`${ghost} shrink-0`}>
                          {scene.busy ? <FiLoader className="animate-spin" size={13} /> : <FiZap size={13} />} Draw it
                        </button>
                      </div>
                      {scene.message && <p className="text-xs text-amber-200 mt-2">{scene.message}</p>}
                      <p className="text-xs text-gray-500 mt-2">Drawing takes 10 seconds to a few minutes, depending on how busy the free image services are.</p>
                    </div>
                    {!bg.token && <p className="text-xs text-gray-500">Until you choose a picture, the background stays transparent.</p>}
                  </div>
                )}
                {s.background.type === "transparent" && <p className="text-xs text-gray-500">The subject on a transparent background. Download as PNG or WebP to keep it.</p>}
              </div>
            )}

            {tab === "subject" && (
              <div className={`${cardClass} space-y-5`}>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Slider label="Edge: shrink (-) / grow (+)" value={s.subject.edge} min={-10} max={10} onChange={(v) => patch("subject", { edge: v })} />
                  <Slider label="Soften the edge" value={s.subject.feather} min={0} max={10} onChange={(v) => patch("subject", { feather: v })} />
                  <Slider label="Size" value={s.subject.scale} min={25} max={250} onChange={(v) => patch("subject", { scale: v })} suffix="%" />
                  <Slider label="Turn" value={s.subject.rotate} min={-180} max={180} onChange={(v) => patch("subject", { rotate: v })} suffix="°" />
                  <Slider label="Move left / right" value={s.subject.x} min={-60} max={60} onChange={(v) => patch("subject", { x: v })} />
                  <Slider label="Move up / down" value={s.subject.y} min={-60} max={60} onChange={(v) => patch("subject", { y: v })} />
                </div>
                <p className="text-xs text-gray-500">If a light halo is left around the subject, shrink the edge a little; if hair or fur was cut too much, grow it.</p>
                <div className="grid sm:grid-cols-2 gap-5 border-t border-navy-border pt-5">
                  <Slider label="Brightness" value={s.subject.brightness} min={-100} max={100} onChange={(v) => patch("subject", { brightness: v })} />
                  <Slider label="Contrast" value={s.subject.contrast} min={-100} max={100} onChange={(v) => patch("subject", { contrast: v })} />
                  <Slider label="Colour" value={s.subject.saturation} min={-100} max={100} onChange={(v) => patch("subject", { saturation: v })} />
                  <Slider label="Warmth (cool - warm)" value={s.subject.warmth} min={-100} max={100} onChange={(v) => patch("subject", { warmth: v })} />
                  <Slider label="Sharpness" value={s.subject.sharpness} min={0} max={100} onChange={(v) => patch("subject", { sharpness: v })} />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => patch("subject", { enhance: !s.subject.enhance })} aria-pressed={s.subject.enhance} className={chip(s.subject.enhance)}>Auto enhance</button>
                  <button type="button" onClick={() => patch("subject", { grey: !s.subject.grey })} aria-pressed={s.subject.grey} className={chip(s.subject.grey)}>Black &amp; white</button>
                  <button type="button" onClick={() => patch("subject", { flip: !s.subject.flip })} aria-pressed={s.subject.flip} className={chip(s.subject.flip)}>Flip</button>
                  <button type="button" onClick={() => patch("subject", { fill_holes: !s.subject.fill_holes })} aria-pressed={s.subject.fill_holes} className={chip(s.subject.fill_holes)}>Fill holes</button>
                </div>
              </div>
            )}

            {tab === "shadow" && (
              <div className={`${cardClass} space-y-6`}>
                <div>
                  <span className={labelClass}>Shadow</span>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {[["none", "None"], ["drop", "Drop shadow"], ["contact", "Floor shadow"]].map(([key, label]) => <button key={key} type="button" onClick={() => patch("shadow", { type: key })} aria-pressed={s.shadow.type === key} className={chip(s.shadow.type === key)}>{label}</button>)}
                  </div>
                  {s.shadow.type !== "none" && (
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Slider label="Strength" value={s.shadow.opacity} onChange={(v) => patch("shadow", { opacity: v })} suffix="%" />
                      <Slider label="Softness" value={s.shadow.blur} min={0} max={50} onChange={(v) => patch("shadow", { blur: v })} />
                      {s.shadow.type === "drop" && <><Slider label="Move sideways" value={s.shadow.x} min={-30} max={30} onChange={(v) => patch("shadow", { x: v })} /><Slider label="Move down" value={s.shadow.y} min={-30} max={30} onChange={(v) => patch("shadow", { y: v })} /></>}
                      <Colour label="Shadow colour" value={s.shadow.color} onChange={(v) => patch("shadow", { color: v })} />
                    </div>
                  )}
                  {s.shadow.type === "contact" && <p className="text-xs text-gray-500 mt-3">A soft pool under the subject - good for products on a white background.</p>}
                </div>
                <div className="border-t border-navy-border pt-5">
                  <span className={labelClass}>Outline (sticker look)</span>
                  <div className="grid sm:grid-cols-2 gap-5 items-end">
                    <Slider label="Outline width" value={s.outline.width} min={0} max={20} onChange={(v) => patch("outline", { width: v })} />
                    <Colour label="Outline colour" value={s.outline.color} onChange={(v) => patch("outline", { color: v })} />
                  </div>
                </div>
              </div>
            )}

            {tab === "size" && (
              <div className={`${cardClass} space-y-5`}>
                <div>
                  <span className={labelClass}>Frame</span>
                  <div className="flex gap-2 mb-4">
                    {[["keep", "Keep the original frame"], ["fit", "Fit the subject to a shape"]].map(([key, label]) => <button key={key} type="button" onClick={() => patch("canvas", { mode: key })} aria-pressed={s.canvas.mode === key} className={chip(s.canvas.mode === key)}>{label}</button>)}
                  </div>
                  {s.canvas.mode === "fit" && (
                    <>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {(options?.aspects || []).map((a) => <button key={a.key} type="button" onClick={() => patch("canvas", { aspect: a.key })} aria-pressed={s.canvas.aspect === a.key} className={chip(s.canvas.aspect === a.key)}>{a.label}</button>)}
                      </div>
                      <Slider label="Space around the subject" value={s.canvas.padding} min={0} max={40} onChange={(v) => patch("canvas", { padding: v })} suffix="%" />
                    </>
                  )}
                </div>
                <div>
                  <span className={labelClass}>Output size (long side)</span>
                  <div className="flex flex-wrap gap-2">
                    {(options?.sizes || []).map((a) => <button key={a.key} type="button" onClick={() => patch("canvas", { size: a.key })} aria-pressed={s.canvas.size === a.key} className={chip(s.canvas.size === a.key)}>{a.label}</button>)}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">Larger than the original does not add detail; use the Upscaling tool for that.</p>
                </div>
              </div>
            )}

            {tab === "download" && (
              <div className={`${cardClass} space-y-5`}>
                <div>
                  <span className={labelClass}>File type</span>
                  <div className="flex flex-wrap gap-2">
                    {(options?.formats || []).map((f) => <button key={f.key} type="button" onClick={() => setFormat(f.key)} aria-pressed={format === f.key} className={chip(format === f.key)}>{f.label}</button>)}
                  </div>
                  {format === "jpg" && s.background.type === "transparent" && <p className="text-xs text-amber-200 mt-3">JPG cannot be transparent: the empty area will be filled with white. Choose a background colour first, or use PNG.</p>}
                </div>
                {error && <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">{error}</p>}
                <button type="button" onClick={download} disabled={exporting}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-60">
                  {exporting ? <><FiLoader className="animate-spin" /> Making the file...</> : <><FiDownload /> Download picture</>}
                </button>
                {note && <p className="text-xs text-emerald-300">{note}</p>}
                <p className="text-xs text-gray-500">The file is made at full size. Your upload is deleted from our server after a few hours; the AI cut-out step sends it to a free outside service (AI Horde).</p>
              </div>
            )}

            <div className="flex items-center justify-between mt-5">
              <button type="button" onClick={reset} className={ghost}><FiRotateCcw size={13} /> Reset changes</button>
              {tab !== "download" && <button type="button" onClick={() => setTab("download")} className="px-5 py-2.5 rounded-full bg-brand-gradient text-white text-sm font-semibold shadow-md shadow-accent/20">Next: download</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
